import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  Spin,
  Table,
  Form,
  Modal,
  message,
  Breadcrumb,
  Result,
  Tabs,
} from "antd";
import { BorderlessTableOutlined, CalendarOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { useMediaQuery } from "react-responsive";
import requireAuth from "../../hocs/requireAuth";
import {
  fetchBreakDownReason,
  fetchBreakDowns,
} from "../../redux/actions/breakdownActions";
import { fetchCompanies } from "../../redux/actions/companyActions";
import HeaderSelector from "../../components/breakdownSetting/headerSelector";
import BreakDownSettingForm from "../../components/breakdownSetting/breakdownSettingForm";
import { SUPER_ADMIN_ROLE_ACCESS, ORGANISATION_ADMIN_ROLE_IDs, ORGANISATION_MANAGER_IDs, ORGANISATION_SUPERVISORS_IDs, ORGANISATION_OPERATOR_IDs } from "../../constants/userRoles";

import get from "lodash/get";
import find from "lodash/find";
import isEmpty from "lodash/isEmpty";
import map from "lodash/map";

import { getBreakdownColumns } from "../../constants/tableColumns/getBreakdownColumns";
import {
  FAILED_FETCH_BREAKDOWN,
  FAILED_FETCH_COMPANINES,
  FETCH_BREAKDOWN_REASON_ERROR,
  SELECT_TEMPLATE,
} from "../../constants/message";
import { convertTimeRangeToAmPm } from "../../utilities/helpers";
import { useLocation } from "react-router-dom";
import { fetchCompanyTemplates } from "../../redux/actions/templateActions";
import { getQueryParams } from "../../utilities/getAndOnParams";
import {
  BREAKDOWN_PERMISSION,
  COMPANY_ADMIN_ROLE_ACCESS,
} from "../../constants";

const initialState = {
  company: undefined,
  templateType: undefined,
  template: undefined,
  date: dayjs(),
};

const PageBreadcrumb = () => {
  return (
    <Breadcrumb
      style={{ fontSize: "14px" }}
      items={[
        {
          title: <BorderlessTableOutlined style={{ fontSize: "16px" }} />,
        },
        {
          title: (
            <>
              <CalendarOutlined style={{ fontSize: "16px" }} />
              <span>Breakdown</span>
            </>
          ),
        },
        {
          title: "Breakdown Settings",
        },
      ]}
    />
  );
};

const BreakdownSettingScreen = () => {
  const [form] = Form.useForm();
  const [showEmpty, setShowEmpty] = useState(false);
  const [reasons, setReasons] = useState(null);
  const [selectedShift, setSelectedShift] = useState(undefined);
  const [loading, setLoading] = useState(false);
  const [config, setConfig] = useState(null);
  const [dataSource, setDataSource] = useState([]);
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const location = useLocation();
  const { company_uuid, template_type, template } = getQueryParams(
    location.search,
  );
  const [filter, setFilters] = useState(initialState);
  const dispatch = useDispatch();
  const [breakdownModal, setBreakdownModal] = useState({
    visible: false,
    data: undefined,
    isEdit: false,
  });

  const setinitialCompany = (company) => {
    const hasPermission = find(get(company, "feature_permissions", []), [
      "key",
      BREAKDOWN_PERMISSION,
    ]);

    if (!hasPermission) return;

    setFilters({
      ...filter,
      templateType: undefined,
      company,
    });
    setConfig({
      ...config,
      companies: [company],
      template_types: get(company, "template_access_list"),
    });
    setSelectedShift(get(company, "company_shifts.0"));
    form.setFieldsValue({
      template: undefined,
      template_type: undefined,
      Company: get(company, "id"),
    });
  };

  const getCompanies = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((result) => {
        if (get(result, "length") === 1) {
          setinitialCompany(result[0]);
        } else {
          setConfig({ companies: result });
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        message.error(FAILED_FETCH_COMPANINES);
      });
  };

  const getBreakdowns = (templateId, date) => {
    if (!templateId) {
      message.warning(SELECT_TEMPLATE);
      return;
    }

    setLoading(true);
    dispatch(fetchBreakDowns(templateId, { date: date }))
      .then((result) => {
        if (!isEmpty(result)) {
          setShowEmpty(false);
          setDataSource(result);
        } else {
          setDataSource([]);
          setShowEmpty(true);
        }
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
        message.error(FAILED_FETCH_BREAKDOWN);
      });
  };

  const toggleBreakDownModal = (data = undefined, isEdit = false) => {
    setBreakdownModal({
      visible: !get(breakdownModal, "visible"),
      data,
      isEdit,
    });
  };

  const showEditModal = (record) => {
    toggleBreakDownModal(record, get(record, "break_down_reason.id"));
  };

  const getReasons = () => {
    dispatch(fetchBreakDownReason())
      .then((result) => {
        setReasons(get(result, "breakdown_reasons"));
      })
      .catch(() => {
        message.error(FETCH_BREAKDOWN_REASON_ERROR);
      });
  };

  const initializeFormParams = () => {
    setLoading(true);
    dispatch(fetchCompanies())
      .then((companies) => {
        const company = find(companies, ["id", company_uuid]);

        if (!company) {
          message.error("Company not found from URL param.");
          setLoading(false);
          return;
        }

        form.setFieldsValue({
          Company: company_uuid,
          template_type: parseInt(template_type),
          template,
          date: dayjs(),
        });

        dispatch(
          fetchCompanyTemplates(company_uuid, {
            type: parseInt(template_type),
          }),
        )
          .then((templates) => {
            setConfig((prev) => ({
              ...prev,
              companies,
              template_types: get(company, "template_access_list"),
              templates,
            }));

            setFilters((prev) => ({
              ...prev,
              company,
              templateType: template_type,
              template,
              date: dayjs(),
            }));

            getBreakdowns(template, dayjs().format("YYYY-MM-DD"));
          })
          .finally(() => {
            setLoading(false);
          });
      })
      .catch(() => {
        message.error(FAILED_FETCH_COMPANINES);
        setLoading(false);
      });
  };

  useEffect(() => {
    getCompanies();
    getReasons();

    if (company_uuid && template_type && template) {
      initializeFormParams();
    }
  }, []);

  return (
    <div className={`${isMobile ? "" : "p-2"} h-full flex flex-col`}>
      <div className="flex flex-row justify-between pb-4">
        <PageBreadcrumb />
      </div>

      <div className="h-full rounded-xl overflow-hidden bg-white flex-1 overflow-y-auto scrollbar-hidden pb-5 min-w-full">
        <Spin spinning={loading}>
          <HeaderSelector
            form={form}
            filter={filter}
            config={config}
            setConfig={setConfig}
            setFilters={setFilters}
            setDataSource={setDataSource}
            getDetails={() => {
              getBreakdowns(
                get(filter, "template"),
                dayjs(get(filter, "date")).format("YYYY-MM-DD"),
              );
            }}
            setSelectedShift={setSelectedShift}
          />
          {get(dataSource, "length") > 0 && (
            <div className="w-full px-4 md:px-8 py-6">
              <h2 className="text-xl font-bold mb-8 text-black-1">
                {get(
                  find(get(config, "templates"), [
                    "id",
                    get(filter, "template"),
                  ]),
                  "name",
                )}
              </h2>
              <Tabs
                tabBarGutter={10}
                tabPosition={"top"}
                type="card"
                destroyOnHidden
                activeKey={get(selectedShift, "id")}
                onTabClick={(key) => {
                  setSelectedShift(
                    find(get(filter, "company.company_shifts"), ["id", key]),
                  );
                }}
                items={map(get(filter, "company.company_shifts"), (shift) => {
                  return {
                    key: get(shift, "id"),
                    label: get(shift, "name"),
                    children: (
                      <>
                        <div className="w-full py-6">
                          <Table
                            columns={getBreakdownColumns(showEditModal)}
                            dataSource={get(
                              find(dataSource, ["id", get(shift, "id")]),
                              "slots",
                            )}
                            pagination={false}
                            size="small"
                            rowKey={(record) =>
                              get(record, "id") || crypto.randomUUID()
                            }
                          />
                        </div>
                      </>
                    ),
                  };
                })}
                onChange={() => { }}
              />
            </div>
          )}
          {showEmpty && (
            <div className="px-8 py-8">
              <Result
                title={`No Breakdown exists...!`}
                subTitle="Please try again with another company or template type."
                status="404"
              />
            </div>
          )}
          <Modal
            title={`${get(breakdownModal, "isEdit") ? "Edit " : "Add "
              } Breakdown: ${convertTimeRangeToAmPm(
                get(breakdownModal, "data.slot", "00:00-00:00"),
              )}`}
            open={get(breakdownModal, "visible")}
            onCancel={() =>
              setBreakdownModal({
                visible: false,
                data: undefined,
                isEdit: false,
              })
            }
            destroyOnHidden
            width={600}
            onOk={() => { }}
            footer={false}
          >
            {get(breakdownModal, "visible") && get(breakdownModal, "data") && (
              <BreakDownSettingForm
                filter={filter}
                breakdownModal={breakdownModal}
                reasons={reasons}
                toggle={() => toggleBreakDownModal()}
                breakdowns={get(
                  find(dataSource, ["id", get(selectedShift, "id")]),
                  "slots",
                )}
                getBreakdowns={() =>
                  getBreakdowns(
                    get(filter, "template"),
                    dayjs(get(filter, "date")).format("YYYY-MM-DD"),
                  )
                }
              />
            )}
          </Modal>
        </Spin>
      </div>
    </div>
  );
};

export default requireAuth(BreakdownSettingScreen, [
  ...SUPER_ADMIN_ROLE_ACCESS,
  ...ORGANISATION_ADMIN_ROLE_IDs,
  ...ORGANISATION_MANAGER_IDs,
  ...ORGANISATION_SUPERVISORS_IDs,
  ...ORGANISATION_OPERATOR_IDs
]);
